import { useEffect, useRef, useCallback, useState } from 'react';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE = 60 * 1000; // 60 seconds warning before logout

const ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'keypress'
];

/**
 * useInactivityTimeout
 * Tracks user inactivity and calls onTimeout after INACTIVITY_TIMEOUT ms.
 * Calls onWarning 60 seconds before timeout.
 * Returns { timeoutWarning, remainingSeconds, resetTimer }
 */
const useInactivityTimeout = ({ onTimeout, onWarning, enabled = true }) => {
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);

  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const clearAllTimers = useCallback(() => {
    if (logoutTimerRef?.current) clearTimeout(logoutTimerRef?.current);
    if (warningTimerRef?.current) clearTimeout(warningTimerRef?.current);
    if (countdownRef?.current) clearInterval(countdownRef?.current);
    logoutTimerRef.current = null;
    warningTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const startCountdown = useCallback(() => {
    setRemainingSeconds(60);
    if (countdownRef?.current) clearInterval(countdownRef?.current);
    countdownRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef?.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabledRef?.current) return;
    clearAllTimers();
    setTimeoutWarning(false);
    setRemainingSeconds(60);

    warningTimerRef.current = setTimeout(() => {
      setTimeoutWarning(true);
      startCountdown();
      onWarning?.();
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);

    logoutTimerRef.current = setTimeout(() => {
      setTimeoutWarning(false);
      clearAllTimers();
      onTimeout?.();
    }, INACTIVITY_TIMEOUT);
  }, [clearAllTimers, startCountdown, onTimeout, onWarning]);

  // Dismiss warning and reset timer (user acknowledged)
  const dismissWarning = useCallback(() => {
    setTimeoutWarning(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setTimeoutWarning(false);
      return;
    }

    resetTimer();

    const handleActivity = () => {
      if (!timeoutWarning) {
        resetTimer();
      }
    };

    ACTIVITY_EVENTS?.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS?.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { timeoutWarning, remainingSeconds, resetTimer, dismissWarning };
};

export default useInactivityTimeout;
